import React, { useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import {
  Button,
  Drawer,
  InputNumber,
  Spin,
  Switch,
  Table,
  Tag,
  Typography,
} from 'antd';
// Api
import { GET_USER_FEATURE_LIMITS } from 'api/featureLimits/queries';
import {
  RESET_USER_FEATURE_LIMIT,
  SET_USER_FEATURE_LIMIT,
} from 'api/featureLimits/mutations';
import { FeatureLimitKey, FeatureLimitKind } from 'api/graphql-global-types';
// Helpers
import {
  FEATURE_LIMIT_LABELS,
  ResolvedFeatureLimit,
  TOGGLE_STATE_LABELS,
  formatLimit,
} from '../../constants';
// Ui
import { successNotification, errorNotification } from 'ui/Notification';
// Styles
import styles from './UserFeatureLimitsDrawer.module.scss';

const { Text } = Typography;

const SOURCE_TAG_COLORS: Record<ResolvedFeatureLimit['source'], string> = {
  Override: 'orange',
  Global: 'blue',
  None: 'default',
};

const SOURCE_TAG_LABELS: Record<ResolvedFeatureLimit['source'], string> = {
  Override: 'Override',
  Global: 'Global',
  None: 'Unlimited (no config)',
};

type UserFeatureLimitsDrawerProps = {
  userId: string | null;
  userName: string;
  open: boolean;
  onClose: () => void;
};

const UserFeatureLimitsDrawer = ({
  userId,
  userName,
  open,
  onClose,
}: UserFeatureLimitsDrawerProps): JSX.Element => {
  const [drafts, setDrafts] = useState<
    Partial<Record<FeatureLimitKey, number | null>>
  >({});
  const [busyKey, setBusyKey] = useState<FeatureLimitKey | null>(null);

  const { data, loading, refetch } = useQuery(GET_USER_FEATURE_LIMITS, {
    variables: { userId },
    skip: !open || !userId,
    fetchPolicy: 'cache-and-network',
  });

  const [setUserFeatureLimit] = useMutation(SET_USER_FEATURE_LIMIT);
  const [resetUserFeatureLimit] = useMutation(RESET_USER_FEATURE_LIMIT);

  const limits: ResolvedFeatureLimit[] = data?.userFeatureLimits ?? [];

  const handleSave = async (featureKey: FeatureLimitKey) => {
    if (!userId) return;
    const draft = drafts[featureKey];
    if (draft === undefined) return;
    setBusyKey(featureKey);
    try {
      await setUserFeatureLimit({
        variables: {
          input: { userId, featureKey, monthlyLimit: draft },
        },
      });
      successNotification(
        `${FEATURE_LIMIT_LABELS[featureKey]} override set to ${formatLimit(
          draft
        )}`
      );
      setDrafts((prev) => ({ ...prev, [featureKey]: undefined }));
      refetch();
    } catch (error) {
      errorNotification((error as Error).message);
    } finally {
      setBusyKey(null);
    }
  };

  const handleToggle = async (
    featureKey: FeatureLimitKey,
    enabled: boolean
  ) => {
    if (!userId) return;
    setBusyKey(featureKey);
    try {
      await setUserFeatureLimit({
        variables: {
          input: { userId, featureKey, monthlyLimit: enabled ? null : 0 },
        },
      });
      const stateLabels = TOGGLE_STATE_LABELS[featureKey];
      successNotification(
        `${FEATURE_LIMIT_LABELS[featureKey]}: ${
          enabled ? stateLabels?.on : stateLabels?.off
        }`
      );
      refetch();
    } catch (error) {
      errorNotification((error as Error).message);
    } finally {
      setBusyKey(null);
    }
  };

  const handleReset = async (featureKey: FeatureLimitKey) => {
    if (!userId) return;
    setBusyKey(featureKey);
    try {
      await resetUserFeatureLimit({
        variables: { userId, featureKey },
      });
      successNotification(
        `${FEATURE_LIMIT_LABELS[featureKey]} override removed — user follows the global default again`
      );
      refetch();
    } catch (error) {
      errorNotification((error as Error).message);
    } finally {
      setBusyKey(null);
    }
  };

  const columns = [
    {
      title: 'Feature',
      key: 'feature',
      width: 200,
      render: (_: unknown, record: ResolvedFeatureLimit) =>
        FEATURE_LIMIT_LABELS[record.featureKey] ?? record.featureKey,
    },
    {
      title: 'Effective limit',
      key: 'limit',
      width: 110,
      render: (_: unknown, record: ResolvedFeatureLimit) => (
        <Text type={record.monthlyLimit === 0 ? 'danger' : undefined}>
          {record.kind === FeatureLimitKind.Toggle
            ? record.monthlyLimit === 0
              ? TOGGLE_STATE_LABELS[record.featureKey]?.off
              : TOGGLE_STATE_LABELS[record.featureKey]?.on
            : formatLimit(record.monthlyLimit)}
        </Text>
      ),
    },
    {
      title: 'Source',
      key: 'source',
      width: 140,
      render: (_: unknown, record: ResolvedFeatureLimit) => (
        <Tag color={SOURCE_TAG_COLORS[record.source]}>
          {record.source === 'None' && record.kind === FeatureLimitKind.Toggle
            ? 'Default'
            : SOURCE_TAG_LABELS[record.source]}
        </Tag>
      ),
    },
    {
      title: 'Used this month',
      key: 'used',
      width: 110,
      render: (_: unknown, record: ResolvedFeatureLimit) =>
        record.kind === FeatureLimitKind.Toggle
          ? '—'
          : record.remaining === null
          ? `${record.used}`
          : `${record.used} (${record.remaining} left)`,
    },
    {
      title: 'Override (empty = unlimited, 0 = disabled)',
      key: 'actions',
      render: (_: unknown, record: ResolvedFeatureLimit) => (
        <div className={styles.actionsCell}>
          {record.kind === FeatureLimitKind.Toggle ? (
            <Switch
              checked={record.monthlyLimit !== 0}
              loading={busyKey === record.featureKey}
              onChange={(checked) => handleToggle(record.featureKey, checked)}
            />
          ) : (
            <>
              <InputNumber
                min={0}
                precision={0}
                placeholder="Unlimited"
                size="small"
                value={drafts[record.featureKey] ?? undefined}
                onChange={(value) =>
                  setDrafts((prev) => ({
                    ...prev,
                    [record.featureKey]: value ?? null,
                  }))
                }
              />
              <Button
                type="primary"
                size="small"
                disabled={drafts[record.featureKey] === undefined}
                loading={busyKey === record.featureKey}
                onClick={() => handleSave(record.featureKey)}
              >
                Save
              </Button>
            </>
          )}
          <Button
            size="small"
            disabled={record.source !== 'Override'}
            loading={busyKey === record.featureKey}
            onClick={() => handleReset(record.featureKey)}
          >
            Reset to default
          </Button>
        </div>
      ),
    },
  ];

  return (
    <Drawer
      title={`Feature limits — ${userName}`}
      placement="right"
      width={900}
      visible={open}
      onClose={onClose}
      destroyOnClose
    >
      {loading && !data ? (
        <div className={styles.spinner}>
          <Spin />
        </div>
      ) : (
        <Table<ResolvedFeatureLimit>
          rowKey="featureKey"
          columns={columns}
          dataSource={limits}
          pagination={false}
          size="small"
        />
      )}
    </Drawer>
  );
};

export default UserFeatureLimitsDrawer;
